import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';
import ChairGallery from './components/ChairGallery';

const benefits = [
  {
    icon: 'ri-sofa-line',
    title: 'Longarinas estofadas que aguentam cultos de 3 horas sem cansar o fiel',
    description: 'Assento em espuma D33 de alta densidade com encosto anatômico que apoia a coluna lombar. O fiel pode sentar durante o sermão, a Santa Ceia e o momento de louvor sem sentir desconforto. O conforto mantém a atenção na palavra.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Estrutura reforçada que dura décadas de uso intenso',
    description: 'Estrutura em aço tubular de 1,5mm com pintura eletrostática ou madeira maciça de lei com acabamento em verniz PU. Suporta peso de até 150kg por lugar sem rangir ou ceder. Fabricamos para durar a vida inteira do templo.',
  },
  {
    icon: 'ri-palette-line',
    title: 'Cores sóbrias que harmonizam com qualquer decoração litúrgica',
    description: 'Estofamento em courvin de alta qualidade ou tecido sintético resistente em cores vinho, azul marinho, verde musgo, marrom, bege e preto. Cores que não desbotam com o sol e mantêm a dignidade do ambiente por anos.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem pela nossa equipe, sem interromper os cultos',
    description: 'Instalação feita pela nossa equipe própria em horários que não atrapalham a programação da igreja — durante a semana, em horários alternativos ou entre cultos. Sem terceiros, sem sujeira, sem fechar o templo.',
  },
];

const models = [
  {
    tag: 'Mais vendido',
    tagColor: 'bg-[#E8610A]',
    icon: 'ri-sofa-line',
    title: 'Longarina Estofada',
    subtitle: 'Módulos conectados de 2 a 5 lugares',
    description: 'O modelo clássico das igrejas. Cadeiras unidas em fileira com estrutura em aço tubular e estofamento em courvin ou tecido. Conforto superior para cultos longos, disponível em marrom, bege, vinho, preto e outras cores.',
    highlights: ['Espuma D33 de alta densidade', 'Estrutura em aço tubular 1,5mm', 'Courvin resistente a manchas', 'Módulos de 2 a 5 lugares'],
    img: '',
  },
  {
    tag: 'Melhor custo-benefício',
    tagColor: 'bg-emerald-600',
    icon: 'ri-layout-row-line',
    title: 'Longarina ISO Plástica',
    subtitle: 'Módulos conectados leves e duráveis',
    description: 'Ideal para igrejas que buscam praticidade e economia sem abrir mão da qualidade. Assento e encosto em polipropileno injetado de alta resistência, estrutura metálica robusta. Fácil limpeza, empilhável e muito durável.',
    highlights: ['Polipropileno de alta resistência', 'Fácil limpeza com pano úmido', 'Leve e empilhável', 'Ótimo custo-benefício'],
    img: '',
  },
  {
    tag: 'Modelo auditório',
    tagColor: 'bg-gray-700',
    icon: 'ri-armchair-line',
    title: 'Cadeira Estofada Individual',
    subtitle: 'Cadeiras separadas empilháveis',
    description: 'Cadeiras individuais estofadas com estrutura em aço tubular, assento e encosto acolchoados. Podem ser dispostas em fileiras ou reorganizadas conforme a necessidade do evento. Empilháveis para fácil armazenamento.',
    highlights: ['Assento e encosto acolchoados', 'Estrutura em aço tubular', 'Empilhável — fácil guardar', 'Flexível para eventos'],
    img: '',
  },
  {
    tag: 'Conferenza',
    tagColor: 'bg-[#E8610A]',
    icon: 'ri-stack-line',
    title: 'Cadeira Conferenza',
    subtitle: 'Empilhável com engate enfileirador',
    description: 'Cadeira empilhável com estrutura em tubo oblongo reforçado, solda MIG e pintura epóxi. Assento e encosto com espuma injetada para máximo conforto mesmo em eventos longos. Inclui engate enfileirador para organização perfeita das fileiras.',
    highlights: ['Tubo oblongo reforçado', 'Solda MIG de alta resistência', 'Pintura epóxi anticorrosiva', 'Espuma injetada no assento e encosto', 'Engate enfileirador incluso'],
    img: '/media/ia/0373.webp',
  },
  {
    tag: 'Diretor',
    tagColor: 'bg-gray-700',
    icon: 'ri-user-star-line',
    title: 'Poltrona Diretor',
    subtitle: 'Conforto premium para liderança',
    description: 'Poltrona diretor com estrutura em tubo oblongo reforçado, solda MIG e pintura epóxi. Assento e encosto com espuma injetada que garante conforto prolongado para pastores, bispos e líderes durante longos cultos e reuniões ministeriais.',
    highlights: ['Tubo oblongo reforçado', 'Solda MIG de alta resistência', 'Pintura epóxi anticorrosiva', 'Espuma injetada no assento e encosto', 'Design executivo ergonômico'],
    img: '/media/ia/0374.webp',
  },
  {
    tag: 'Meet',
    tagColor: 'bg-red-600',
    icon: 'ri-vip-crown-line',
    title: 'Poltrona Meet',
    subtitle: 'Garantia de 7 anos · Até 200kg',
    description: 'A cadeira mais resistente da linha. Suporta até 200kg por assento com estrutura em tubo de aço e engate de aço reforçado. Assento e encosto em compensado naval com espuma injetada D55. Ideal para igrejas que exigem durabilidade extrema.',
    highlights: ['Garantia de 7 anos', 'Suporta até 200kg por assento', 'Engate de aço reforçado', 'Espuma injetada D55', 'Assento em compensado naval'],
    img: '/media/produtos/0375.png',
  },
];

const blocks = [
  {
    title: 'Longarinas Estofadas que Mantêm o Fiel Confortável durante o Culto Inteiro',
    text: [
      'O culto médio dura entre 2 e 3 horas. O fiel senta, ouve a palavra, participa dos louvores, ora de joelhos e volta a sentar. Uma longarina desconfortável vira distração — o fiel pensa na dor nas costas em vez de na mensagem. Nossas longarinas são projetadas para que o conforto desapareça e só reste a concentração.',
      'Nossas longarinas estofadas para igrejas incluem assento em espuma D33 de alta densidade com 8cm de espessura que não deforma com o tempo, encosto anatômico com curva lombar que apoia a coluna durante longos períodos sentado, estrutura em aço tubular de 1,5mm com pintura eletrostática anticorrosiva, estofamento em courvin resistente a manchas e desgaste, e opções de 2, 3, 4 ou 5 lugares por módulo para adaptar a qualquer largura de nave.',
    ],
    img: '',
    reverse: false,
  },
  {
    title: 'Estrutura Reforçada que Suporta Gerações de Congregação',
    text: [
      'Uma igreja não troca de mobiliário a cada 5 anos. A longarina precisa aguentar domingo após domingo, culto após culto, geração após geração. Nossas estruturas são projetadas para durar décadas sem rangir, ceder ou enferrujar — porque o templo merece mobiliário que acompanhe sua história.',
      'Utilizamos estrutura em aço tubular de 1,5mm com solda MIG reforçada nos pontos de maior tensão, pintura eletrostática em forno a 200°C que resiste à umidade e ao suor, pés niveladores que compensam irregularidades no piso de concreto ou cerâmica, fixação opcional no piso para maior estabilidade, e acabamento em madeira maciça de lei com verniz PU para igrejas que preferem o toque natural. Testamos cada longarina antes de entregar.',
    ],
    img: '',
    reverse: true,
  },
  {
    title: 'Estofamento em Cores Sóbrias que Valorizam o Ambiente Litúrgico',
    text: [
      'A cor das longarinas define a atmosfera do templo. Cores muito vibrantes distraem. Cores muito claras sujam fácil. Nossas opções de estofamento foram escolhidas para manter a dignidade do ambiente litúrgico sem exigir manutenção constante.',
      'Oferecemos courvin de alta qualidade que resiste a manchas de suor, água e poeira — ideal para o clima quente de Pernambuco, cores vinho, azul marinho, verde musgo, marrom tabaco, bege e preto que harmonizam com qualquer decoração, costura reforçada com linha de nylon que não arrebenta com o uso intenso, e fácil limpeza com pano úmido e álcool 70 — porque o zelador da igreja já tem o que fazer.',
    ],
    img: '',
    reverse: false,
  },
];

const galleryImages: { src: string; alt: string }[] = [];

export default function IgrejasPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Longarinas para Igreja Caruaru PE | Bancos Estofados – Home Office Premium',
    description: 'Longarinas e bancos estofados para igrejas em Caruaru PE. Espuma D33, courvin resistente, estrutura reforçada. Projeto 3D gratuito e montagem pela nossa equipe. Atendemos igrejas no Agreste e Sertão de Pernambuco.',
    keywords: 'longarinas para igreja Recife, bancos para igreja Petrolina, cadeiras para templo Garanhuns, longarinas estofadas Arcoverde, mobiliário para igreja Sertão PE, bancos estofados evangélicos Pernambuco, longarina para culto Agreste, cadeiras igreja Nordeste, longarinas igreja Recife preço, bancos igreja Petrolina, cadeiras templo Garanhuns, mobiliário igreja Caruaru, longarinas Bezerros, bancos estofados Santa Cruz do Capibaribe',
    path: '/igrejas',
  });

  useSchemaLD({
    id: 'schema-igrejas',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Longarinas Estofadas para Igrejas | Caruaru PE',
      description: 'Longarinas estofadas confortáveis e duráveis para igrejas. Estrutura reforçada, espuma D33, courvin resistente. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/igrejas`,
      about: {
        '@type': 'Service',
        name: 'Longarinas Estofadas para Igrejas',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0376.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Longarinas Estofadas para Igrejas
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Longarinas estofadas que mantêm seu povo confortável durante todo o culto.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Espuma D33 de alta densidade, estrutura reforçada em aço ou madeira maciça, estofamento em courvin resistente em cores sóbrias. Porque o fiel que está confortável, presta atenção na palavra.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Longarinas%20Estofadas%20para%20Igrejas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que pastores e líderes escolhem nossas <span className="text-[#E8610A]">longarinas estofadas</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Models */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#E8610A] text-xs font-bold uppercase tracking-widest">Linha completa</span>
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold mt-2">
              Conheça nossos <span className="text-[#E8610A]">modelos disponíveis</span>
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Do modelo mais econômico ao premium — temos a opção certa para o tamanho e orçamento da sua igreja.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {models.map((m) => (
              <div key={m.title} className="bg-white rounded-xl flex flex-col border border-gray-200 overflow-hidden">
                <div className="w-full h-52 overflow-hidden bg-gray-50">
                  <img src={m.img} alt={m.title} className={`w-full h-full hover:scale-105 transition-transform duration-500 ${m.title === 'Poltrona Diretor' || m.title === 'Cadeira Conferenza' ? 'object-contain object-center p-2' : 'object-cover object-center'}`} />
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`${m.tagColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap`}>{m.tag}</span>
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-bold text-lg leading-snug">{m.title}</h3>
                    <p className="text-gray-400 text-xs mt-0.5">{m.subtitle}</p>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{m.description}</p>
                  <div className="flex flex-col gap-1.5 mt-1">
                    {m.highlights.map((h) => (
                      <div key={h} className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-[#E8610A] text-sm"></i>
                        </div>
                        <span className="text-gray-600 text-xs">{h}</span>
                      </div>
                    ))}
                  </div>
                  <a
                    href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20tenho%20interesse%20no%20modelo%20de%20longarina%20para%20minha%20igreja%20e%20quero%20solicitar%20um%20or%C3%A7amento!"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto pt-4 inline-flex items-center gap-2 justify-center bg-[#E8610A] hover:bg-[#C4500A] text-white font-semibold px-4 py-2.5 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-xs uppercase tracking-wide"
                  >
                    <i className="ri-whatsapp-line text-sm"></i>
                    Solicitar orçamento
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chair Gallery - Conferenza e Diretor */}
      <section className="py-16 bg-white">
        <ChairGallery />
      </section>

      {/* Payment & Upholstery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Consulte nossas <span className="text-[#E8610A]">formas de pagamento</span> e os revestimentos disponíveis
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
              Facilitamos a aquisição das suas longarinas com condições especiais e revestimentos que se adaptam às necessidades do seu templo.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Payment methods */}
            <div className="bg-white rounded-xl p-8 border border-gray-200 flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className="ri-bank-card-line text-[#E8610A] text-xl"></i>
                </div>
                <h3 className="text-gray-900 font-bold text-lg">Formas de pagamento</h3>
              </div>
              <ul className="flex flex-col gap-3">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Cartão de crédito</strong> — em até 12x no parcelamento</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>PIX</strong> — à vista com desconto especial</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Boleto bancário</strong> — pagamento à vista ou parcelado</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Financiamento</strong> — condições especiais para igrejas sob consulta</span>
                </li>
              </ul>
            </div>
            {/* Upholstery options */}
            <div className="bg-white rounded-xl p-8 border border-gray-200 flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className="ri-paint-brush-line text-[#E8610A] text-xl"></i>
                </div>
                <h3 className="text-gray-900 font-bold text-lg">Revestimentos disponíveis</h3>
              </div>
              <ul className="flex flex-col gap-3">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Courvin (vinil)</strong> — resistente a manchas e umidade, ideal para o clima de Pernambuco</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Tecido sintético</strong> — alta durabilidade e respirabilidade, disponível em diversas cores</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Couro natural</strong> — acabamento premium sob consulta</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-[#E8610A]"></i>
                  </div>
                  <span className="text-gray-600 text-sm"><strong>Cores disponíveis</strong> — vinho, azul marinho, verde musgo, marrom, bege, preto e sob consulta</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Personalization Section */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left: image */}
            <div className="w-full lg:w-1/2 h-80 md:h-96 rounded-2xl overflow-hidden flex-shrink-0">
              <img
                src="/media/ia/0377.webp"
                alt="Longarinas personalizadas com nome e logo da igreja"
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Right: content */}
            <div className="w-full lg:w-1/2 flex flex-col gap-6">
              <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider w-fit">
                Personalização exclusiva
              </span>
              <h2 className="text-gray-900 text-2xl md:text-3xl font-bold leading-tight">
                Sua cor. Seu nome. Sua identidade em cada cadeira.
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Cada igreja tem uma identidade única — e suas longarinas podem refletir isso. Personalizamos cor do estofamento, estrutura e até o nome ou logotipo da sua congregação bordado ou impresso no encosto de cada cadeira.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-gray-200 flex flex-col gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                    <i className="ri-palette-line text-[#E8610A] text-xl"></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-sm">Cor personalizada</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">Escolha qualquer cor de estofamento — vinho, bege, azul, verde, preto, marrom ou a cor da identidade visual da sua igreja.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-200 flex flex-col gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                    <i className="ri-quill-pen-line text-[#E8610A] text-xl"></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-sm">Nome ou logo bordado</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">O nome ou logotipo da sua congregação bordado ou impresso no encosto de cada cadeira. Um detalhe que transmite identidade e pertencimento.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-200 flex flex-col gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                    <i className="ri-paint-brush-line text-[#E8610A] text-xl"></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-sm">Estrutura na cor certa</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">A estrutura metálica também pode ser pintada na cor que combina com o ambiente — preto, prata, dourado ou qualquer outra cor sob consulta.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-200 flex flex-col gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                    <i className="ri-draft-line text-[#E8610A] text-xl"></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-sm">Projeto 3D antes de produzir</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">Você aprova a cor, o bordado e o layout antes de fabricarmos uma única peça. Sem surpresas, sem retrabalho.</p>
                </div>
              </div>
              <a
                href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20quero%20personalizar%20as%20longarinas%20com%20a%20cor%20e%20o%20nome%20da%20minha%20igreja!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                QUERO PERSONALIZAR MINHAS LONGARINAS
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Longarinas Estofadas</span> que Transformam a Experiência do Culto
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Longarinas%20Estofadas%20para%20Igrejas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-gray-900 text-3xl md:text-4xl font-bold mb-8">
            Mais de <span className="text-[#E8610A]">10 anos</span> fornecendo longarinas estofadas para igrejas do Agreste
          </h2>
          <p className="text-gray-600 text-base leading-relaxed mb-5">
            A <strong>Home Office Premium</strong> entende que para uma igreja, a longarina é o mobiliário mais importante. É onde o fiel passa 2 a 3 horas a cada culto, onde recebe a palavra, onde ora e onde se sente parte da congregação. Atendemos igrejas em Caruaru, Santa Cruz do Capibaribe, Toritama, Brejo da Madre de Deus e todo o Agreste Pernambucano.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-10">
            Nosso processo garante zero surpresas: visita técnica para medir a nave, projeto 3D mostrando a disposição das longarinas, fabricação com materiais de primeira linha e instalação pela nossa equipe — em horários que não atrapalham os cultos. Você não precisa se preocupar com nada — só com a congregação.
          </p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Longarinas%20Estofadas%20para%20Igrejas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#E8610A] hover:bg-[#C4500A] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            FALAR COM UM ESPECIALISTA
          </a>
        </div>
      </section>

      {/* Extended Auditorium Gallery */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#E8610A] text-xs font-bold uppercase tracking-widest">Galeria de ambientes</span>
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold mt-2">
              Auditórios com <span className="text-[#E8610A]">longarinas e cadeiras</span> — veja como fica
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
              Cada templo tem um tamanho e um estilo. Veja como diferentes modelos se encaixam em igrejas pequenas, médias e grandes.
            </p>
          </div>

          {/* Row 1: 2 large side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="relative w-full h-72 rounded-2xl overflow-hidden group">
              <img
                src="/media/ia/0378.webp"
                alt="Grande auditório evangélico com longarinas bege em fileiras"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Auditório grande — longarinas conectadas em fileiras</p>
              </div>
            </div>
            <div className="relative w-full h-72 rounded-2xl overflow-hidden group">
              <img
                src="/media/ia/0379.webp"
                alt="Igreja média com longarinas marrom e corredor central"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Igreja média — longarinas marrom com corredor central</p>
              </div>
            </div>
          </div>

          {/* Row 2: 3 medium */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="relative w-full h-56 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0380.webp"
                alt="Igreja pequena com cadeiras individuais estofadas cinza"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-semibold">Igreja pequena — cadeiras individuais</p>
              </div>
            </div>
            <div className="relative w-full h-56 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0381.webp"
                alt="Vista em perspectiva do auditório com longarinas bege em direção ao palco"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-semibold">Vista em perspectiva — longarinas bege</p>
              </div>
            </div>
            <div className="relative w-full h-56 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0382.webp"
                alt="Igreja com longarinas vinho estofadas em fileiras"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-semibold">Longarinas vinho — elegância litúrgica</p>
              </div>
            </div>
          </div>

          {/* Row 3: wide + square */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div className="relative md:col-span-3 w-full h-64 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0383.webp"
                alt="Auditório amplo com longarinas bege e vitrais"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Auditório amplo — longarinas bege com corredor duplo</p>
              </div>
            </div>
            <div className="relative md:col-span-2 w-full h-64 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0384.webp"
                alt="Detalhe da longarina bege com 4 lugares e estrutura metálica"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Detalhe — longarina 4 lugares estofada</p>
              </div>
            </div>
          </div>

          {/* Row 4: 3 images */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative w-full h-56 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0385.webp"
                alt="Cadeiras individuais bege em fileiras com corredor central"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-semibold">Cadeiras individuais bege — flexíveis e elegantes</p>
              </div>
            </div>
            <div className="relative w-full h-56 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0386.webp"
                alt="Vista lateral de nave com longarinas marrom e ventiladores"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-semibold">Vista lateral — longarinas marrom em nave</p>
              </div>
            </div>
            <div className="relative w-full h-56 rounded-xl overflow-hidden group">
              <img
                src="/media/ia/0387.webp"
                alt="Igreja moderna com cadeiras individuais cinza e iluminação moderna"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-semibold">Igreja moderna — cadeiras individuais cinza</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-10">
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vi%20as%20fotos%20dos%20audit%C3%B3rios%20e%20quero%20saber%20qual%20modelo%20combina%20com%20minha%20igreja!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              QUERO SABER QUAL MODELO COMBINA COM MINHA IGREJA
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA — Catálogo e Preços */}
      <section className="py-20 bg-[#fafafa]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 flex flex-col items-center text-center gap-6">
            <div className="w-16 h-16 flex items-center justify-center bg-[#25D366]/10 rounded-full">
              <i className="ri-whatsapp-line text-[#25D366] text-3xl"></i>
            </div>
            <div>
              <p className="text-[#E8610A] text-xs font-bold uppercase tracking-widest mb-2">Catálogo completo + preços</p>
              <h2 className="text-gray-900 text-2xl md:text-3xl font-bold leading-tight mb-3">
                Quer ver todos os modelos e valores?

                Fale comigo agora pelo WhatsApp.
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-lg mx-auto">
                Mando o catálogo completo com todos os modelos disponíveis, opções de cores, tamanhos e os melhores preços do Agreste — direto no seu WhatsApp. Sem formulário, sem espera.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
              <a
                href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vi%20a%20p%C3%A1gina%20de%20longarinas%20para%20igrejas%20e%20quero%20receber%20o%20cat%C3%A1logo%20completo%20com%20modelos%20e%20pre%C3%A7os!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-8 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                QUERO RECEBER O CATÁLOGO E PREÇOS
              </a>
            </div>
            <p className="text-gray-400 text-xs">Resposta em minutos · Sem compromisso · Atendimento personalizado</p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">
            SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO
          </p>
          <h2 className="text-gray-900 text-3xl md:text-4xl font-bold mb-4">
            Pronto para equipar sua igreja com as melhores longarinas?
          </h2>
          <p className="text-gray-500 text-base mb-2">Fale comigo agora. Mando o catálogo completo, modelos disponíveis e os melhores preços — direto no seu WhatsApp.</p>
          <p className="text-gray-400 text-sm mb-8">Projeto 3D gratuito · Resposta em minutos · Sem compromisso</p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vi%20a%20p%C3%A1gina%20de%20longarinas%20para%20igrejas%20e%20quero%20receber%20o%20cat%C3%A1logo%20completo%20com%20modelos%20e%20pre%C3%A7os!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-5 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-base mb-10"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            FALAR AGORA E RECEBER O CATÁLOGO
          </a>
          <div className="mt-6 text-gray-400 text-sm leading-relaxed">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" />
            </a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" />
            </a>
          </div>
        </div>
      </footer>

      <a
        ref={whatsappBtnRef}
        href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Longarinas%20Estofadas%20para%20Igrejas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200"
        aria-label="WhatsApp"
        style={{ display: 'flex' }}
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
